/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   3/14/22
 */

import React from "react";
import {
	ButtonGroup,
	CircularProgress,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Stack,
	Tooltip
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SaveIcon from "@mui/icons-material/Save";
import SaveAsIcon from "@mui/icons-material/SaveAs";
import SettingsIcon from "@mui/icons-material/Settings";
import DownloadDoneIcon from "@mui/icons-material/DownloadDone";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import { useClient } from "urql";
import {
	CreateSavedCalendarMutation,
	CreateSavedCalendarResponse,
	CreateSavedCalendarVars,
	SavedCalendarInput,
	UpdateSavedCalendarMutation,
	UpdateSavedCalendarResponse,
	UpdateSavedCalendarVars
} from "../graphql/mutations/calendars";
import { useDispatch, useSelector } from "react-redux";
import { State } from "../state/store";
import { set as setCalendar } from "../state/reducers/calendar";
import { set as setEmployeesFilter } from "../state/reducers/employees-filter";
import { defaultSettings, set as setSettings } from "../state/reducers/settings";
import { useNavigate } from "react-router-dom";
import { Calendar } from "../interfaces/Global";
import SettingsSaveAsDialog from "./SettingsSaveAsDialog";
import { set as setDefaultCalendarId } from "../state/reducers/default-calendar-id";
import CalendarSettingsDialog from "./CalendarSettingsDialog";
import { toast } from "react-toastify";

export interface CalendarActionsProps {
	savedCalendar?: Calendar | null
	unsavedChanges: boolean
}

export default function CalendarActions({ savedCalendar, unsavedChanges }: CalendarActionsProps) {

	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const [settingsKey, setSettingsKey] = React.useState(0);
	const [saveInProgress, setSaveInProgress] = React.useState(false);
	const [dialog, setDialog] = React.useState<string|null>(null);
	const [settingsDialogOpen, setSettingsDialogOpen] = React.useState(false);

	const user = useSelector((s:State) => s.user);
	const defaultCalendarId = useSelector((s:State) => s.defaultCalendarId);
	const navigate = useNavigate();
	const stateCalendar = useSelector((s:State) => s.calendar);
	const dispatch = useDispatch();
	const client = useClient();
	const filter = useSelector((s:State) => s.employeesFilter);
	const settings = useSelector((s:State) => s.settings);

	const openMenu = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const closeMenu = () => {
		setAnchorEl(null);
	};

	const openDialog = (dialog: string) => {
		closeMenu();
		setDialog(dialog);
	}

	const canEdit = !stateCalendar.id || savedCalendar?.createdBy === user?.id;

	const saveCalendar = () => {
		const data: SavedCalendarInput = {
			title: stateCalendar.title,
			isPublic: true,
			config: {
				employeesFilter: filter,
				settings
			}
		};
		const promise = !stateCalendar.id ?
			client.mutation<CreateSavedCalendarResponse, CreateSavedCalendarVars>(CreateSavedCalendarMutation, {data}).toPromise().then(r => r.data?.createSavedCalendar) :
			client.mutation<UpdateSavedCalendarResponse, UpdateSavedCalendarVars>(UpdateSavedCalendarMutation, {id: stateCalendar.id, data}).toPromise().then(r => r.data?.updateSavedCalendar);
		closeMenu();
		setSaveInProgress(true);
		promise.then(calendar => {
			if (calendar) {
				if (!stateCalendar.id) {
					navigate("/calendar/" + calendar.id);
					if (!defaultCalendarId) {
						dispatch(setDefaultCalendarId(calendar.id));
					}
				}
				dispatch(setCalendar({ id: calendar.id, title: calendar.title }));
				toast.success(`Calendar "${calendar.title}" (${calendar.id}) has been saved successfully`);
			}
		}).finally(() => setSaveInProgress(false));
	}

	return (
		<Stack direction={"row"} spacing={1}>
			<Tooltip title={"Calendar settings"}>
				<IconButton onClick={() => setSettingsDialogOpen(true)}>
					<SettingsIcon />
				</IconButton>
			</Tooltip>
			{ unsavedChanges && savedCalendar?.config && canEdit &&
				<ButtonGroup variant={"contained"}>
					<Tooltip title={"Commit changes"}>
						<IconButton
							disabled={saveInProgress}
							onClick={saveCalendar}
							color={'success'}
						>
							{saveInProgress ? <CircularProgress size={16} /> : <CheckIcon />}
						</IconButton>
					</Tooltip>
					<Tooltip title={"Discard changes"}>
						<IconButton
							disabled={saveInProgress}
							onClick={() => {
								dispatch(setCalendar({ id: savedCalendar.id, title: savedCalendar.title }));
								dispatch(setEmployeesFilter(savedCalendar.config?.employeesFilter || []));
								dispatch(setSettings(savedCalendar?.config?.settings || defaultSettings));
								setSettingsKey(key => ++key);
							}}
							color={'error'}
						>
							<ClearIcon />
						</IconButton>
					</Tooltip>
				</ButtonGroup>}
			<CalendarSettingsDialog
				key={settingsKey}
				open={settingsDialogOpen}
				onClose={() => setSettingsDialogOpen(false)}
				savedCalendar={savedCalendar}
			/>
			<IconButton onClick={openMenu}>
				<MoreVertIcon />
			</IconButton>
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={closeMenu}
			>
				<MenuItem
					onClick={saveCalendar}
					disabled={!canEdit}
				>
					<ListItemIcon>
						<SaveIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText>Save</ListItemText>
				</MenuItem>
				<MenuItem
					onClick={() => openDialog("save-as")}
				>
					<ListItemIcon>
						<SaveAsIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText>Save As</ListItemText>
				</MenuItem>
				<MenuItem
					onClick={() => {
						closeMenu();
						dispatch(setDefaultCalendarId(stateCalendar.id));
					}}
					disabled={stateCalendar.id === defaultCalendarId}
				>
					<ListItemIcon>
						<DownloadDoneIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText>Make default</ListItemText>
				</MenuItem>
			</Menu>
			<SettingsSaveAsDialog
				open={dialog === "save-as"}
				onClose={() => setDialog(null)}
			/>

		</Stack>
	);
}